import * as React from 'react';

import { Button, Text } from '@/hen-ui/atomics';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Flex } from '@/hen-ui/composites/Flex';
import { LogoBox } from '@/hen-ui/composites/LogoBox';
import { Section } from '@/hen-ui/composites/Section';
import { cn } from '@/hen-ui/utils';

import {
  comparisonBarActionsGroupVariants,
  comparisonBarClearButtonVariants,
  comparisonBarCollapsedContentVariants,
  comparisonBarCollapseToggleVariants,
  comparisonBarCompareButtonVariants,
  comparisonBarContentRowVariants,
  comparisonBarLogoSlotsRowVariants,
  comparisonBarMetaRowVariants,
  comparisonBarMetaStatusVariants,
  comparisonBarMobileHeaderStatusVariants,
  comparisonBarMobileHeaderVariants,
  comparisonBarPanelVariants,
  comparisonBarRemoveButtonLayoutVariants,
  comparisonBarSelectionClusterVariants,
  comparisonBarSlotLayoutVariants,
  comparisonBarTabletBodyVariants,
  comparisonBarTabletStatusVariants,
  comparisonBarViewportSpacerVariants,
  comparisonBarVariants,
  type ComparisonBarVariantProps,
  type ComparisonBarClearButtonVariantProps,
} from './comparison-bar.variants';

export interface ComparisonBarItem {
  id: string;
  schoolLogo: string;
  schoolName: string;
  programName: string;
  onRemove: () => void;
}

export interface ComparisonBarProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'>,
    ComparisonBarVariantProps {
  items: ComparisonBarItem[];
  maxItems?: number;
  minItemsToCompare?: number;
  statusText?: string;
  compareLabel?: string;
  onCompare?: () => void;
  clearLabel?: string;
  onClear?: () => void;
  reserveViewportSpace?: boolean;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  expandLabel?: string;
  collapseLabel?: string;
}

const DEFAULT_MAX_ITEMS = 3;
const DEFAULT_MIN_ITEMS_TO_COMPARE = 2;

const getSlotCount = (maxItems: number) => Math.max(1, Math.trunc(maxItems));

const getMinimumCompareCount = (minItemsToCompare: number, slotCount: number) =>
  Math.min(slotCount, Math.max(1, Math.trunc(minItemsToCompare)));

const slotCountLabels: Record<number, string> = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
};

const getSlotCountLabel = (slotCount: number) =>
  slotCountLabels[slotCount] ?? String(slotCount);

const getStatusText = ({
  selectedCount,
  slotCount,
  minItemsToCompare,
  hasOverflow,
}: {
  selectedCount: number;
  slotCount: number;
  minItemsToCompare: number;
  hasOverflow: boolean;
}) => {
  if (selectedCount >= slotCount || hasOverflow) {
    return `Maximum of ${slotCount} schools selected`;
  }

  if (selectedCount > 0 && selectedCount < minItemsToCompare) {
    return `Choose at least ${minItemsToCompare} schools to compare`;
  }

  return `Add up to ${getSlotCountLabel(slotCount)} schools to compare`;
};

export const ComparisonBar = React.forwardRef<HTMLElement, ComparisonBarProps>(
  (
    {
      items,
      maxItems = DEFAULT_MAX_ITEMS,
      minItemsToCompare = DEFAULT_MIN_ITEMS_TO_COMPARE,
      statusText: customStatusText,
      compareLabel = 'See Comparison',
      onCompare,
      clearLabel = 'Clear all',
      onClear,
      reserveViewportSpace = false,
      collapsed,
      defaultCollapsed = false,
      onCollapsedChange,
      expandLabel = 'Show selected programs',
      collapseLabel = 'Hide selected programs',
      position,
      className,
      'aria-label': ariaLabel = 'Program comparison bar',
      ...props
    },
    ref,
  ) => {
    const [internalCollapsed, setInternalCollapsed] =
      React.useState(defaultCollapsed);
    const contentId = React.useId();
    const slotCount = getSlotCount(maxItems);
    const minimumCompareCount = getMinimumCompareCount(
      minItemsToCompare,
      slotCount,
    );
    const visibleItems = items.slice(0, slotCount);
    const emptySlots = Math.max(slotCount - visibleItems.length, 0);
    const hasOverflow = items.length > slotCount;
    const isCollapsed = collapsed ?? internalCollapsed;
    const canCompare = visibleItems.length >= minimumCompareCount;
    const shouldReserveViewportSpace =
      reserveViewportSpace && (position ?? 'fixed') === 'fixed';
    const showClear = Boolean(onClear && visibleItems.length > 0);
    const statusText =
      customStatusText ??
      getStatusText({
        selectedCount: visibleItems.length,
        slotCount,
        minItemsToCompare: minimumCompareCount,
        hasOverflow,
      });

    const handleCollapsedChange = () => {
      const nextCollapsed = !isCollapsed;
      if (collapsed === undefined) {
        setInternalCollapsed(nextCollapsed);
      }
      onCollapsedChange?.(nextCollapsed);
    };

    const handleCompare = () => {
      if (!canCompare) {
        return;
      }

      onCompare?.();
    };

    const statusTextProps = {
      variant: 'b5' as const,
      color: 'heading' as const,
      weight: 'bold' as const,
      tag: 'p' as const,
      'aria-live': 'polite' as const,
      'aria-atomic': true as const,
    };

    const renderStatus = (
      layoutClassName?: string,
      align?: React.ComponentProps<typeof Text>['align'],
    ) => (
      <Text {...statusTextProps} align={align} className={layoutClassName}>
        {statusText}
      </Text>
    );

    const renderClear = (
      placement: NonNullable<
        ComparisonBarClearButtonVariantProps['placement']
      > = 'meta',
    ) =>
      showClear ? (
        <Button
          type='button'
          variant='textOnlyLink'
          size='flush'
          onClick={onClear}
          className={comparisonBarClearButtonVariants({ placement })}
        >
          {clearLabel}
        </Button>
      ) : null;

    const logoSlots = (
      <>
        {visibleItems.map((item) => (
          <Section
            key={item.id}
            as='div'
            variant='white-outline'
            gap='xxs'
            padding='none'
            rounded='sm'
            data-comparison-bar-slot='selected'
            className={comparisonBarSlotLayoutVariants({ state: 'selected' })}
          >
            <LogoBox
              src={item.schoolLogo}
              alt={`${item.schoolName} logo`}
              size='fill'
              shadow='none'
            />
            <Text tag='span' variant='b5' className='sr-only'>
              {item.schoolName}: {item.programName}
            </Text>
            <Button
              type='button'
              aria-label={`Remove ${item.programName} at ${item.schoolName} from comparison`}
              onClick={item.onRemove}
              variant='iconOnlyChip'
              size='sm'
              iconBefore='x'
              className={comparisonBarRemoveButtonLayoutVariants()}
            />
          </Section>
        ))}

        {Array.from({ length: emptySlots }, (_, index) => (
          <Section
            key={`empty-${index}`}
            as='div'
            variant='white-outline'
            gap='xxs'
            padding='none'
            rounded='sm'
            data-comparison-bar-slot='empty'
            className={comparisonBarSlotLayoutVariants({ state: 'empty' })}
          >
            <Text tag='span' variant='b5' className='sr-only'>
              Empty comparison slot
            </Text>
          </Section>
        ))}
      </>
    );

    const bar = (
      <section
        ref={ref}
        aria-label={ariaLabel}
        className={cn(comparisonBarVariants({ position }), className)}
        {...props}
        data-hen-id={COMPONENT_IDS.ComparisonBar}
      >
        <Section
          as='div'
          variant='elevated-footer'
          gap='min'
          padding='page-bar'
          className={comparisonBarPanelVariants()}
        >
          <Flex
            width='full'
            className={comparisonBarMobileHeaderVariants()}
            data-comparison-bar-mobile-header
          >
            {renderStatus(comparisonBarMobileHeaderStatusVariants(), 'center')}
            <Button
              type='button'
              size='sm'
              aria-expanded={!isCollapsed}
              aria-controls={contentId}
              onClick={handleCollapsedChange}
              variant='textOnlyNeutral'
              iconBefore={isCollapsed ? 'chevron-up' : 'chevron-down'}
              className={comparisonBarCollapseToggleVariants()}
            >
              <Text tag='span' variant='b5' className='sr-only'>
                {isCollapsed ? expandLabel : collapseLabel}
              </Text>
            </Button>
          </Flex>

          <Flex
            id={contentId}
            direction='column'
            gap='min'
            width='full'
            className={comparisonBarCollapsedContentVariants({
              collapsed: isCollapsed,
            })}
          >
            <Flex
              direction='column'
              direction-lg='row'
              align='stretch'
              align-md='center'
              gap='sm'
              width='full'
              className={comparisonBarContentRowVariants()}
            >
              <Flex
                direction='column'
                align='center'
                gap='min'
                width='full'
                className={comparisonBarSelectionClusterVariants()}
                data-comparison-bar-selection-cluster
              >
                <Text
                  {...statusTextProps}
                  align='center'
                  className={comparisonBarTabletStatusVariants()}
                  data-comparison-bar-tablet-status
                >
                  {statusText}
                </Text>

                <Flex
                  direction='column'
                  direction-md='row'
                  align='stretch'
                  align-md='center'
                  justify='between'
                  gap='sm'
                  width='full'
                  className={comparisonBarTabletBodyVariants()}
                  data-comparison-bar-tablet-body
                >
                  <Flex
                    direction='row'
                    align='stretch'
                    justify='center'
                    gap='xs'
                    width='full'
                    className={comparisonBarLogoSlotsRowVariants()}
                    data-comparison-bar-logo-slots
                  >
                    {logoSlots}
                  </Flex>

                  <Flex
                    className={comparisonBarMetaRowVariants()}
                    data-comparison-bar-meta
                  >
                    {renderStatus(comparisonBarMetaStatusVariants())}
                    {renderClear('meta')}
                  </Flex>

                  <Flex
                    direction='column'
                    align='center'
                    gap='min'
                    className={comparisonBarActionsGroupVariants()}
                    data-comparison-bar-actions-group
                  >
                    <Button
                      type='button'
                      variant='blackFill'
                      size='sm'
                      disabled={!canCompare}
                      onClick={handleCompare}
                      className={comparisonBarCompareButtonVariants()}
                    >
                      {compareLabel}
                    </Button>
                    {renderClear('actions')}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Section>
      </section>
    );

    if (!shouldReserveViewportSpace) {
      return bar;
    }

    return (
      <>
        <Flex
          aria-hidden='true'
          className={comparisonBarViewportSpacerVariants()}
        />
        {bar}
      </>
    );
  },
);

ComparisonBar.displayName = 'ComparisonBar';
